import { useState, useContext, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import UserContext from "../../../contexts/UserContext";
import ChatFullscreenContext from "../../../contexts/ChatFullscreenContext";
import { useWebSocketContext } from "../../../contexts/WebSocketContext";
import LeadConversationPanel from "../../../components/leads/conversation/LeadConversationPanel";
import ChatErrorFallback from "../../../components/leads/chat/ChatErrorFallback";
import ErrorBoundary from "../../../components/ErrorBoundary";
import NewConversationModal from "../../../components/leads/chat/NewConversationModal";
import InboxListPanel, {
  type InboxRow,
  type InboxView,
  type InboxMessageKind,
} from "../../../components/inbox/InboxListPanel";
import type { InboxFilterState } from "../../../components/inbox/InboxFilters";
import { useIsMobile, useMediaQuery } from "../../../hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { contactDisplay, leadDisplayName, type ContactInput } from "@/lib/contact";
import { isDataScopedEmployee } from "@/utils/permissions";
import type { RouterOutputs } from "../../../trpc";

const VIEW_STORAGE_KEY = "palomma.inbox.view";
const OWN_EMPLOYEE_STORAGE_KEY = "palomma.inbox.ownEmployee";
const FILTERS_STORAGE_KEY = "palomma.inbox.filters";
const PAGE_SIZE = 50;

const EMPTY_FILTERS: InboxFilterState = {};

const readPersistedFilters = (merchantId: string): InboxFilterState => {
  if (typeof window === "undefined" || !merchantId) return EMPTY_FILTERS;
  try {
    const raw = window.localStorage.getItem(`${FILTERS_STORAGE_KEY}.${merchantId}`);
    if (!raw) return EMPTY_FILTERS;
    const parsed = JSON.parse(raw) as Partial<InboxFilterState>;
    return {
      pipelineId: typeof parsed.pipelineId === "string" ? parsed.pipelineId : undefined,
      pipelinePositionId:
        typeof parsed.pipelinePositionId === "string" ? parsed.pipelinePositionId : undefined,
      employeeId: typeof parsed.employeeId === "string" ? parsed.employeeId : undefined,
      source: typeof parsed.source === "string" ? parsed.source : undefined,
      tagIds: Array.isArray(parsed.tagIds)
        ? parsed.tagIds.filter((id): id is string => typeof id === "string")
        : undefined,
    };
  } catch {
    return EMPTY_FILTERS;
  }
};

type RawInboxRow = RouterOutputs["inbox"]["list"]["data"][number];

const byRecencyDesc = (a: RawInboxRow, b: RawInboxRow): number =>
  new Date(b.lastLeadMessageAt ?? b.createdAt).getTime() -
  new Date(a.lastLeadMessageAt ?? a.createdAt).getTime();

const isView = (v: string | null): v is InboxView =>
  v === "all" || v === "mine" || v === "team" || v === "ai";

const readPersistedView = (allowTeam: boolean): InboxView => {
  if (typeof window === "undefined") return "mine";
  const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
  if (!isView(raw)) return "mine";
  if (raw === "team" && !allowTeam) return "mine";
  return raw;
};

const readPersistedOwnEmployeeId = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    return window.localStorage.getItem(OWN_EMPLOYEE_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
};

const formatName = (name: string | null | undefined, lead: ContactInput): string => {
  const trimmed = (name || "").trim();
  // Inbox rows now carry both handles on every page (the Tinybird endpoint
  // selects them too), so a nameless phoneless lead resolves to its @handle
  // instead of "Sin nombre" — and never to the raw id sitting in `phone`.
  if (!trimmed) return leadDisplayName(lead);
  return trimmed
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

const InboxContainer = (): JSX.Element => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { merchantId } = useContext(MerchantContext);
  const {
    roles,
    pipelinePermissions,
    employeeId: userEmployeeId,
    employees: userEmployees,
    employeeIds,
    isLoading: isLoadingUser,
  } = useContext(UserContext);
  const { setFullscreen } = useContext(ChatFullscreenContext);

  // Restricted employees inherit basic_employee's inbox scoping (own view, pipeline-limited).
  const isBasicEmployeeRoles = isDataScopedEmployee(roles);
  const [view, setView] = useState<InboxView>(() => readPersistedView(!isBasicEmployeeRoles));
  const [page, setPage] = useState(0);
  const [pages, setPages] = useState<Map<number, RawInboxRow[]>>(new Map());
  // The ?id= deep link is read ONCE, at mount. After this the URL is derived
  // output (see the mirror effect below), so re-reading it live — as this used to —
  // would turn the one-shot context reset into a standing instruction that re-fires
  // every time the user opens another conversation.
  const [initialLeadId] = useState<string | undefined>(() => searchParams.get("id") ?? undefined);
  const [selectedLeadId, setSelectedLeadId] = useState<string | undefined>(initialLeadId);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<InboxFilterState>(() => readPersistedFilters(merchantId));
  const isMobile = useIsMobile();
  // Three columns need roughly 1200px of usable width (the collapsed SideMenu
  // takes 80px): 340 list + 380 perfil leaves the conversation ~480px at exactly
  // 1280. Below that the perfil moves behind its header button rather than
  // squeezing. Note this measures the viewport, so an expanded SideMenu (256px)
  // eats another 176px the query can't see — the conversation column gets tight
  // between 1280 and ~1456 until the rail is collapsed again.
  const isWide = useMediaQuery("(min-width: 1280px)");
  const [mobileView, setMobileView] = useState<"list" | "chat">("list");
  const [isNewConversationModalOpen, setIsNewConversationModalOpen] = useState(false);
  const [storedOwnEmployeeId, setStoredOwnEmployeeId] = useState<string | undefined>(
    readPersistedOwnEmployeeId
  );
  const ownEmployeeId =
    storedOwnEmployeeId && employeeIds.includes(storedOwnEmployeeId)
      ? storedOwnEmployeeId
      : employeeIds[0];

  const handleOwnEmployeeChange = (id: string) => {
    setStoredOwnEmployeeId(id);
    try {
      window.localStorage.setItem(OWN_EMPLOYEE_STORAGE_KEY, id);
    } catch {
      return;
    }
  };

  const { data: employees } = trpc.employees.list.useQuery(
    { merchantId },
    { enabled: !!merchantId, staleTime: 5 * 60 * 1000 }
  );
  const { data: pipelinesData } = trpc.pipeline.list.useQuery(
    { merchantId },
    { enabled: !!merchantId, staleTime: 5 * 60 * 1000 }
  );
  const { data: leadTags } = trpc.leadTags.list.useQuery(
    { merchantId },
    { enabled: !!merchantId, staleTime: 5 * 60 * 1000 }
  );
  const { data: agentsData } = trpc.agents.list.useQuery(
    { merchantId },
    { enabled: !!merchantId, staleTime: 5 * 60 * 1000 }
  );
  const allowedPipelineIdSet = useMemo(() => {
    if (!isBasicEmployeeRoles) return null;
    return new Set(pipelinePermissions.map((p) => p.id));
  }, [isBasicEmployeeRoles, pipelinePermissions]);

  const pipelines = useMemo(() => {
    const list = (pipelinesData ?? []).map((p) => ({ id: p.id, name: p.name }));
    if (!allowedPipelineIdSet) return list;
    return list.filter((p) => allowedPipelineIdSet.has(p.id));
  }, [pipelinesData, allowedPipelineIdSet]);

  const employeeOptions = useMemo(
    () => (employees ?? []).map((e) => ({ id: e.id, name: e.name })),
    [employees]
  );
  const tagOptions = useMemo(
    () =>
      (leadTags ?? []).map((t) => ({
        id: t.id,
        name: t.status === "archived" ? `${t.name} (archivada)` : t.name,
      })),
    [leadTags]
  );
  const employeeNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of employees ?? []) map.set(e.id, e.name);
    return map;
  }, [employees]);

  const isBasicEmployee = isBasicEmployeeRoles;
  const hasNoPermissions = isBasicEmployee && pipelinePermissions.length === 0;
  const showTeamTab = !isBasicEmployee;
  const effectiveView: InboxView = view === "team" && !showTeamTab ? "mine" : view;

  const allowedPipelineIds = isBasicEmployee ? pipelinePermissions.map((p) => p.id) : undefined;
  const pipelineIdsParam = useMemo(() => {
    if (filters.pipelineId) {
      if (allowedPipelineIds && !allowedPipelineIds.includes(filters.pipelineId)) {
        return allowedPipelineIds;
      }
      return [filters.pipelineId];
    }
    return allowedPipelineIds;
  }, [filters.pipelineId, allowedPipelineIds]);
  const pipelinePositionIdsParam = filters.pipelinePositionId
    ? [filters.pipelinePositionId]
    : undefined;
  const sourcesParam = filters.source ? [filters.source] : undefined;
  const tagIdsParam = filters.tagIds?.length ? filters.tagIds : undefined;
  const trimmedSearch = search.trim();
  const searchParam = trimmedSearch.length > 0 ? trimmedSearch : undefined;

  const queryTab: "assigned" | "ai" | "all" =
    effectiveView === "ai" ? "ai" : effectiveView === "all" ? "all" : "assigned";
  const employeeIdParam =
    effectiveView === "mine"
      ? ownEmployeeId
      : effectiveView === "team"
      ? filters.employeeId
      : undefined;

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(VIEW_STORAGE_KEY, effectiveView);
  }, [effectiveView]);

  useEffect(() => {
    if (isBasicEmployeeRoles && view !== "mine") setView("mine");
  }, [isBasicEmployeeRoles, view]);

  const viewNeedsPipeline =
    effectiveView === "mine" || (effectiveView === "team" && !!filters.employeeId);
  useEffect(() => {
    if (!viewNeedsPipeline) return;
    if (filters.pipelineId) return;
    if (isBasicEmployeeRoles && pipelinePermissions.length > 0) {
      setFilters((f) => ({ ...f, pipelineId: pipelinePermissions[0].id }));
    } else if (!isBasicEmployeeRoles && pipelines.length > 0) {
      const defaultPipeline = pipelines.find((p) => p.id === "default");
      setFilters((f) => ({ ...f, pipelineId: (defaultPipeline ?? pipelines[0]).id }));
    }
  }, [viewNeedsPipeline, isBasicEmployeeRoles, filters.pipelineId, pipelinePermissions, pipelines]);

  useEffect(() => {
    if (typeof window === "undefined" || !merchantId) return;
    const hasAny =
      !!filters.pipelineId ||
      !!filters.pipelinePositionId ||
      !!filters.employeeId ||
      !!filters.source ||
      !!filters.tagIds?.length;
    const key = `${FILTERS_STORAGE_KEY}.${merchantId}`;
    if (hasAny) window.localStorage.setItem(key, JSON.stringify(filters));
    else window.localStorage.removeItem(key);
  }, [filters, merchantId]);

  const handleViewChange = (next: InboxView) => {
    setView(next);
    setSearch("");
  };

  useEffect(() => {
    setPage(0);
    setPages(new Map());
  }, [
    effectiveView,
    ownEmployeeId,
    merchantId,
    searchParam,
    filters.pipelineId,
    filters.pipelinePositionId,
    filters.employeeId,
    filters.source,
    filters.tagIds?.join(","),
  ]);

  useEffect(() => {
    const isFullscreen = isMobile && mobileView === "chat";
    setFullscreen(isFullscreen);
    return () => setFullscreen(false);
  }, [isMobile, mobileView, setFullscreen]);

  const queryEnabled =
    !!merchantId &&
    !isLoadingUser &&
    roles.length > 0 &&
    !hasNoPermissions &&
    (effectiveView !== "mine" || !!ownEmployeeId) &&
    (!viewNeedsPipeline || !!pipelineIdsParam);

  const baseListInput = useMemo(
    () => ({
      merchantId,
      tab: queryTab,
      employeeId: employeeIdParam,
      pipelineIds: pipelineIdsParam,
      pipelinePositionIds: pipelinePositionIdsParam,
      sources: sourcesParam,
      tagIds: tagIdsParam,
      search: searchParam,
      pageSize: PAGE_SIZE,
    }),
    [
      merchantId,
      queryTab,
      employeeIdParam,
      pipelineIdsParam,
      pipelinePositionIdsParam,
      sourcesParam,
      tagIdsParam,
      searchParam,
    ]
  );

  // Page 0 stays mounted so a WS-triggered inbox invalidation refetches the
  // fresh top in recency mode (DynamoDB), even when the user has scrolled into
  // deeper (Tinybird) pages. Shares React Query's cache key with the paginated
  // query when page === 0, so it is the same request, just always observed.
  //
  // WS invalidation still skips variants with `search` set (WebSocketContext),
  // which used to be about not refetching Tinybird. Search is DynamoDB now, but
  // skipping is still right for a better reason: a search result SET is keyed on
  // handles, and a new message changes neither a phone nor a username — only
  // ordering and preview, which applyDeltasToInboxList already patches into
  // every inbox.list cache entry, search included. The name half
  // (inbox.searchNonRealtime) gets no deltas, which is what its label says.
  const page0Query = trpc.inbox.list.useQuery(
    { ...baseListInput, page: 0 },
    { enabled: queryEnabled, keepPreviousData: true }
  );

  const { data, isFetching } = trpc.inbox.list.useQuery(
    { ...baseListInput, page },
    { enabled: queryEnabled, keepPreviousData: true }
  );

  // The name half of a search. `inbox.list` answers phones and both WhatsApp
  // handles from DynamoDB; a name has no index and can only be matched by
  // Tinybird's substring scan. Fired concurrently rather than after, so the
  // DynamoDB rows paint without waiting on it.
  const nameQuery = trpc.inbox.searchNonRealtime.useQuery(
    { ...baseListInput, page: 0 },
    { enabled: queryEnabled && !!searchParam, keepPreviousData: true }
  );

  useEffect(() => {
    if (!data) return;
    setPages((prev) => {
      const next = new Map(prev);
      next.set(page, data.data);
      return next;
    });
  }, [data, page]);

  const accumulated = useMemo<RawInboxRow[]>(() => {
    const page0 = page0Query.data?.data ?? [];
    const ordered = Array.from(pages.entries()).sort(([a], [b]) => a - b);
    const rest = ordered.flatMap(([, rows]) => rows);
    // Dedup by lead id: the fresh page-0 (DynamoDB) copy comes first and wins
    // over any stale deeper (Tinybird) copy, so a reactivated lead that jumped to
    // the top does not also linger at its old deep position.
    const seen = new Set<string>();
    const deduped: RawInboxRow[] = [];
    for (const row of [...page0, ...rest]) {
      if (seen.has(row.id)) continue;
      seen.add(row.id);
      deduped.push(row);
    }
    // Fresh page-0 and stale deeper pages can disagree on a lead's recency across
    // the seam; sort the deduped union so ordering stays correct (cheap, small n).
    deduped.sort(byRecencyDesc);
    return deduped;
  }, [page0Query.data, pages]);

  // Name matches minus anything DynamoDB already returned — the realtime copy
  // wins a tie, being the fresher row. Kept as its own group rather than merged
  // into `accumulated`, which sorts purely by recency and would interleave the
  // two, leaving no place to say which rows may be stale.
  const nonRealtimeRows = useMemo<RawInboxRow[]>(() => {
    if (!searchParam) return [];
    const seen = new Set(accumulated.map((r) => r.id));
    return (nameQuery.data?.data ?? []).filter((r) => !seen.has(r.id)).sort(byRecencyDesc);
  }, [nameQuery.data, accumulated, searchParam]);

  const nonRealtimeIds = useMemo(
    () => new Set(nonRealtimeRows.map((r) => r.id)),
    [nonRealtimeRows]
  );

  const unreadCountQueryInput = useMemo(
    () => ({
      merchantId,
      tab: "assigned" as const,
      employeeId: ownEmployeeId,
      pipelineIds: allowedPipelineIds,
      page: 0,
      pageSize: 100,
    }),
    [merchantId, ownEmployeeId, allowedPipelineIds]
  );

  const unreadCountQuery = trpc.inbox.list.useQuery(unreadCountQueryInput, {
    enabled: !!merchantId && !!ownEmployeeId && !isLoadingUser && !hasNoPermissions,
  });
  const unreadAssignedCount = (unreadCountQuery.data?.data ?? []).filter((r) => r.unread).length;

  const rows: InboxRow[] | undefined = useMemo(() => {
    if (!queryEnabled) {
      if (effectiveView === "mine" && !isLoadingUser && !ownEmployeeId) {
        return [];
      }
      return undefined;
    }
    // Hold the skeleton until BOTH halves have something or have settled —
    // otherwise an empty-but-fast DynamoDB result flashes "no encontramos
    // conversaciones" while the name match is still in flight.
    const stillLoading = isFetching || nameQuery.isFetching;
    if (stillLoading && accumulated.length === 0 && nonRealtimeRows.length === 0) return undefined;
    // Realtime rows first, then the name matches under their own heading.
    return [...accumulated, ...nonRealtimeRows].map((row): InboxRow => {
      const lastMessageAt = row.lastLeadMessageAt ?? row.createdAt;
      const isAi = !row.employeeId;
      const lastMessage =
        row.lastMessageType && row.lastMessageContent != null
          ? {
              kind: row.lastMessageType as InboxMessageKind,
              content: row.lastMessageContent,
              fromHuman: row.lastMessageSource === "human_agent",
            }
          : null;
      return {
        id: row.id,
        name: formatName(row.name, row),
        lastMessage,
        unread: !!row.unread,
        // Same fact the tablero paints its cards red for, off the same field — the
        // rows already carry lastMessageSource (it is what `fromHuman` reads above),
        // on all three read paths: DynamoDB page 0, Tinybird's deeper pages, and the
        // handle search.
        needsReply: row.lastMessageSource === "customer",
        lastMessageAt,
        assignment: isAi
          ? { kind: "ai" }
          : {
              kind: "assigned",
              assigneeName: employeeNameById.get(row.employeeId ?? "") ?? "Asignado",
              isSelf: !!userEmployeeId && row.employeeId === userEmployeeId,
            },
        tagIds: row.tagIds,
      };
    });
  }, [
    accumulated,
    nonRealtimeRows,
    isFetching,
    nameQuery.isFetching,
    queryEnabled,
    queryTab,
    ownEmployeeId,
    userEmployeeId,
    employeeNameById,
    effectiveView,
    isLoadingUser,
  ]);

  const markAsRead = trpc.inbox.markAsRead.useMutation();
  const utils = trpc.useUtils();
  const { clearRecentDelta } = useWebSocketContext();

  const { data: leadFromQuery } = trpc.leads.get.useQuery(
    { merchantId, id: initialLeadId || "" },
    { enabled: !!merchantId && !!initialLeadId }
  );

  // Arriving on a shared link is a context switch: the lead is very likely outside
  // whatever view and filters this user had, so widen to "all" and search its handle
  // to bring its row into the list beside the conversation.
  //
  // Latched rather than guarded by clearing the URL, which is what used to make it
  // run once. The effect cannot key on live searchParams any more, and it must not
  // re-fire: `leadFromQuery` gets a fresh identity on every refetch and `isMobile`
  // flips on any resize across 768px — either would re-wipe filters the user set
  // after landing, and overwrite the search box under them.
  const didApplyDeepLink = useRef(false);
  useEffect(() => {
    if (didApplyDeepLink.current || !initialLeadId || !leadFromQuery) return;
    didApplyDeepLink.current = true;
    setView("all");
    setFilters(EMPTY_FILTERS);
    // Seed the box with whichever handle the lead actually has. Seeding
    // `phone` alone left a phoneless lead with an empty term, which landed the
    // deep link on an unfiltered inbox — the exact leads this search is for.
    // `copyValue` is the bare handle, never the rendered "@" form.
    const contact = contactDisplay(leadFromQuery);
    setSearch(contact.kind === "none" ? "" : contact.copyValue);
    setSelectedLeadId(leadFromQuery.id);
    if (isMobile) setMobileView("chat");
  }, [initialLeadId, leadFromQuery, isMobile]);

  // The URL mirrors the open conversation, the way the tablero mirrors its own
  // shareable state (TableroContainer). replace: true — clicking through
  // conversations should not pile up back-button entries.
  useEffect(() => {
    const params: Record<string, string> = {};
    if (selectedLeadId) params.id = selectedLeadId;
    setSearchParams(params, { replace: true });
  }, [selectedLeadId, setSearchParams]);

  const handleOpenConversation = (leadId: string) => {
    setSelectedLeadId(leadId);
    if (isMobile) setMobileView("chat");
    clearRecentDelta(leadId);
    setPages((prev) => {
      const next = new Map<number, RawInboxRow[]>();
      prev.forEach((rows, p) => {
        next.set(
          p,
          rows.map((r) => (r.id === leadId ? { ...r, unread: false } : r))
        );
      });
      return next;
    });
    utils.inbox.list.setData({ ...baseListInput, page: 0 }, (prev) =>
      prev
        ? {
            ...prev,
            data: prev.data.map((r) => (r.id === leadId ? { ...r, unread: false } : r)),
          }
        : prev
    );
    markAsRead.mutate({ merchantId, id: leadId });
    utils.inbox.list.setData(unreadCountQueryInput, (prev) =>
      prev
        ? {
            ...prev,
            data: prev.data.map((r) => (r.id === leadId ? { ...r, unread: false } : r)),
          }
        : prev
    );
  };

  const handleBackToList = () => {
    setMobileView("list");
  };

  const handleNewConversation = () => {
    setIsNewConversationModalOpen(true);
  };

  const handleConversationCreated = (leadId: string) => {
    setSelectedLeadId(leadId);
    if (isMobile) setMobileView("chat");
    utils.inbox.list.invalidate();
  };

  if (!merchantId) {
    return (
      <div className="flex h-[calc(100dvh-60px)] items-center justify-center md:h-screen">
        <p className="text-gray-400">No hay comercio seleccionado</p>
      </div>
    );
  }

  if (hasNoPermissions) {
    return (
      <div className="flex h-[calc(100dvh-60px)] items-center justify-center px-6 text-center md:h-screen">
        <p className="max-w-md text-muted-foreground">
          No tienes permisos asignados para ver conversaciones. Contacta con el administrador.
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-row bg-[#F9F9FB]",
        isMobile && mobileView === "chat" ? "h-dvh" : "h-[calc(100dvh-60px)] md:h-screen"
      )}
    >
      {(!isMobile || mobileView === "list") && (
        <div
          className={cn(
            "min-w-0 flex-1 overflow-hidden bg-white md:flex-none md:basis-[340px] md:border-r md:border-border",
            // The perfil column claims 380px, so the list gives back the width it
            // would otherwise take at lg/xl — the conversation is the pane that
            // must not be squeezed.
            isWide ? "lg:basis-[340px]" : "lg:basis-[400px] xl:basis-[480px]"
          )}
        >
          <InboxListPanel
            view={effectiveView}
            onViewChange={handleViewChange}
            showTeamTab={showTeamTab}
            rows={rows}
            nonRealtimeIds={nonRealtimeIds}
            unreadMineCount={unreadAssignedCount}
            hasMore={data?.hasMore ?? false}
            onLoadMore={() => setPage((p) => p + 1)}
            selectedLeadId={selectedLeadId}
            onOpenConversation={handleOpenConversation}
            search={search}
            onSearchChange={setSearch}
            filters={filters}
            onFiltersChange={(next) =>
              setFilters((prev) => {
                const merged = { ...prev, ...next };
                if (
                  isBasicEmployeeRoles &&
                  "pipelineId" in next &&
                  !next.pipelineId &&
                  pipelinePermissions.length > 0
                ) {
                  merged.pipelineId = prev.pipelineId ?? pipelinePermissions[0].id;
                }
                return merged;
              })
            }
            pipelines={pipelines}
            employees={employeeOptions}
            tags={tagOptions}
            isSearching={(isFetching || nameQuery.isFetching) && !!searchParam}
            hideTabs={isBasicEmployeeRoles}
            onNewConversation={handleNewConversation}
            ownEmployees={userEmployees}
            ownEmployeeId={ownEmployeeId}
            onOwnEmployeeChange={handleOwnEmployeeChange}
          />
        </div>
      )}
      {(!isMobile || mobileView === "chat") && (
        <div className="min-w-0 flex-1 overflow-hidden bg-white">
          {selectedLeadId ? (
            // Keyed on the lead so opening another conversation clears a stuck error.
            // The panel carries its own boundary around the timeline + composer; this
            // outer one also covers a crash in the perfil column.
            <ErrorBoundary
              surface="inbox-chat"
              resetKey={selectedLeadId}
              fallback={(args) => <ChatErrorFallback {...args} />}
            >
              <LeadConversationPanel
                key={selectedLeadId}
                leadId={selectedLeadId}
                surface="inbox"
                profileInline={isWide}
                onBack={isMobile ? handleBackToList : undefined}
              />
            </ErrorBoundary>
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-gray-400">Selecciona una conversación</p>
            </div>
          )}
        </div>
      )}
      <NewConversationModal
        isOpen={isNewConversationModalOpen}
        onClose={() => setIsNewConversationModalOpen(false)}
        agents={agentsData || []}
        onConversationCreated={handleConversationCreated}
      />
    </div>
  );
};

export default InboxContainer;
